import { ApplicationSummaryDto, AssignedConfigsDto, ProductTypeEnum } from '@inosio/payments-sdk';
import { LoadingButton } from '@mui/lab';
import { Box, Card, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, Modal, Typography } from '@mui/material';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CancelIcon from '../../../icons/close.svg';
import { createScreeningOrder, resetScreeningOrderState, selectApplicationsState } from '../../../resui/applications/sagas/pm/applicationsSlice';
import { getProducts, selectOnboardingApplicationState } from '../../onboarding/sagas/pmonboardingSlice';
import { convertCentsToDollars } from '../../../shared/util/converters';
import NumberFormat from 'react-number-format';
import DollarSign from '../../../icons/DollarSign.svg';
import back2 from '../../../icons/back2.svg';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import ConfirmIcon from '../../../icons/ConfirmationTick.svg';
import ComingSoon from '../../../icons/ComingSoon.svg';
import { applicationStyles } from '../../../resui/applications/components/CustomStyles';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';

export interface OrderScreeningOptionsModalProps {
  open: boolean;
  onClose: () => void;
  application: ApplicationSummaryDto;
}

interface ScreeningOptionType {
  Id: string;
  Label: string;
  Cost: number;
  IsSelected: boolean;
  PurchasedDate: string;
}

interface ScreeneeDetails {
  Uuid: string;
  DisplayName: string;
  IsSelected: boolean;
}

export default function OrderScreeningOptionsModal(props: OrderScreeningOptionsModalProps) {
  const dispatch = useDispatch();
  const classes = applicationStyles();
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '24px',
    height: '523px',
    pointerEvents: 'auto',
    width: '472px',
    borderRadius: '24px',
  };

  const isApplicationCompleted = props.application.Status !== 'INCOMPLETE';
  const [orderStep, setOrderStep] = useState(1);
  const [orderButtonEnabled, setOrderButtonEnabled] = useState(false);
  const [availableScreeningProducts, setAvailableScreeningProducts] = useState<ScreeningOptionType[]>([]);
  const [availableScreenees, setAvailableScreenees] = useState<ScreeneeDetails[]>([]);
  const [successfulScreeneeOrderResponses, setSuccessfulScreeneeOrderResponses] = useState<string[]>([]);
  const [newItemsToPurchase, setNewItemsToPurchase] = useState<ScreeningOptionType[]>([]);
  const { ProductOptions } = useSelector(selectOnboardingApplicationState);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus] = React.useState<string | null>(null);
  const [globalError, setGlobalError] = React.useState<string | null>(null);
  const {
    isCreatingScreeningOrder,
    isCreatingScreeningOrderSuccess,
    isCreatingScreeningOrderFailed,
    createScreeningOrderError: screeningOrderError,
    createScreeningOrderResponse,
  } = useSelector(selectApplicationsState);
  const { allPropertyGroupsAssignedConfigs } = useSelector(selectPropertyGroupDetailsState);
  const propGroupConfigs = allPropertyGroupsAssignedConfigs?.find((pgc) => pgc.Uuid === props.application.OrganizationUuid) || ({} as AssignedConfigsDto);

  function buildDefaultScreneeList() {
    //todo review this since we updated the API dto to be more clear
    const list: ScreeneeDetails[] = [];
    const primaryApplicant = props.application.Applicants.find((a) => a.Uuid === props.application.PrimaryApplicantUuid);
    const registeredCoApplicants = props.application.Applicants.filter((a) => a.Uuid !== props.application.PrimaryApplicantUuid && a.Status === 'COMPLETED');
    if (primaryApplicant) list.push({ IsSelected: true, DisplayName: `${primaryApplicant.FirstName} ${primaryApplicant.LastName}` || '', Uuid: primaryApplicant.Uuid });

    if (registeredCoApplicants?.length)
      list.push(
        ...registeredCoApplicants.map((pca) => {
          return { DisplayName: `${pca.FirstName} ${pca.LastName}`, IsSelected: true, Uuid: pca.Uuid } as ScreeneeDetails;
        }),
      );
    return list;
  }

  function submitOrder() {
    const applicationUuid = props.application?.Uuid;
    const productsPurchased = newItemsToPurchase.map((np) => np.Id);

    availableScreenees
      .filter((sc) => !!sc.IsSelected)
      .map((ss) => {
        return dispatch({
          type: createScreeningOrder.type,
          payload: {
            ApplicantUuid: ss.Uuid,
            ApplicationUuid: applicationUuid,
            Products: productsPurchased.map(p=>{ return {Uuid: p}}),
          },
        });
      });
  }

  useEffect(() => {
    setIsSpinnerModalOpen(isCreatingScreeningOrder);

    if (createScreeningOrderResponse.ApplicantUuid) {
      const updatedList = [...successfulScreeneeOrderResponses, createScreeningOrderResponse.ApplicantUuid];
      setSuccessfulScreeneeOrderResponses(updatedList);
      if (updatedList.length === availableScreenees.filter((sc) => !!sc.IsSelected).length) {
        setOrderStep(3);
      }
    }

    if (isCreatingScreeningOrderFailed) {
      if (screeningOrderError.statusCode >= 400 && screeningOrderError.statusCode < 500) {
        setGlobalError(screeningOrderError.message);
      } else setGlobalError('Something went wrong. Please try again later or contact support.');
    } else setGlobalError(null);
  }, [isCreatingScreeningOrder, isCreatingScreeningOrderSuccess, isCreatingScreeningOrderFailed, screeningOrderError, createScreeningOrderResponse]);

  useEffect(() => {
    dispatch({ type: resetScreeningOrderState.type });

    if (props?.application?.OrganizationUuid) dispatch({ type: getProducts.type, payload: { organizationUuid: props.application.OrganizationUuid, productType: ProductTypeEnum.Screening } });
    setAvailableScreenees(buildDefaultScreneeList());
  }, [dispatch]);

  useEffect(() => {
    //fires whenever ProductOptions is received or  availableScreenees changes
    const availableALaCarteItems = ProductOptions.filter((po) => ['ScreeningProductALaCarteDto', 'ScreeningProductPackageDto'].includes(po.ClassTemplate)).map((sp) => {
      //* This logic is a little wonky, but it's the best we can do for the modal UI.
      //* The idea is, if any of the selected screenees have this product ordered, then it can't be ordered without first unselecting the ones who have it.
      const atLeastOneSelectedScreeneePreviousPurchasedDate = props.application?.ScreeningOrders?.find((so) => {
        const screeneeIsSelectedAndHasOrderedThisProduct = availableScreenees.some((s) => {
          return s.IsSelected && s.Uuid === so.ApplicantUuid && so.Products.some(p=>p.Uuid ===sp.Uuid);
        });

        return screeneeIsSelectedAndHasOrderedThisProduct;
      });

      return {
        Id: sp.Uuid,
        Label: sp.ProductLabel,
        Cost: sp.CustomPrice || (sp.ProductDetails as any)?.Cost,
        IsSelected: !!atLeastOneSelectedScreeneePreviousPurchasedDate?.CreatedDate,
        PurchasedDate: atLeastOneSelectedScreeneePreviousPurchasedDate?.CreatedDate,
      } as ScreeningOptionType;
    });
    setAvailableScreeningProducts(availableALaCarteItems);

    const isAtLeastOneNewItemSelected = availableALaCarteItems.find((p) => !p.PurchasedDate && p.IsSelected);
    const isAtLeastOneScreeneeSelected = availableScreenees.find((p) => p.IsSelected);
    setOrderButtonEnabled(!!isAtLeastOneScreeneeSelected && !!isAtLeastOneNewItemSelected);
  }, [ProductOptions, availableScreenees]);

  function handleProductSelectionCheckboxChange(event: ChangeEvent<HTMLInputElement>, checked: boolean): void {
    const updatedProductList = availableScreeningProducts.map((p) => {
      if (p.Id === event.target.name) {
        return { ...p, IsSelected: checked };
      }
      return p;
    });
    setAvailableScreeningProducts(updatedProductList);

    const isAtLeastOneNewItemSelected = updatedProductList.find((p) => !p.PurchasedDate && p.IsSelected);
    const isAtLeastOneScreeneeSelected = availableScreenees.find((p) => p.IsSelected);
    setOrderButtonEnabled(!!isAtLeastOneNewItemSelected && !!isAtLeastOneScreeneeSelected);
  }

  function handleScreeneeSelectionCheckboxChange(event: ChangeEvent<HTMLInputElement>, checked: boolean): void {
    const updatedSelectedScreeneeList = availableScreenees.map((p) => {
      if (p.Uuid === event.target.name) {
        return { ...p, IsSelected: checked };
      }
      return p;
    });
    setAvailableScreenees(updatedSelectedScreeneeList);

    const isAtLeastOneNewItemSelected = availableScreeningProducts.find((p) => !p.PurchasedDate && p.IsSelected);
    const isAtLeastOneScreeneeSelected = updatedSelectedScreeneeList.find((p) => p.IsSelected);
    setOrderButtonEnabled(!!isAtLeastOneScreeneeSelected && !!isAtLeastOneNewItemSelected);
  }

  function moveToConfirmationStep() {
    //we say the item is new if it's selected and has no purchase date yet
    setNewItemsToPurchase(availableScreeningProducts.filter((p) => !p.PurchasedDate && p.IsSelected));
    setOrderStep(2);
  }
  const handleCloseSpinnerModal = React.useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  return (
    <div>
      <Modal
        open={props.open}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        BackdropProps={{
          invisible: false,
        }}
        disableEscapeKeyDown
        disableAutoFocus={true}
      >
        <Card id="orderScreeningStep1" sx={style}>
          {orderStep === 1 && propGroupConfigs.ScreeningEnabled && (
            <Grid container width="100%" display="flex" justifyContent="center">
              <Grid item xs={12} display="flex" justifyContent="center">
                <Typography
                  sx={{
                    fontFamily: 'Work Sans !important',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    fontSize: '18px !important',
                    lineHeight: '21px !important',
                    color: '#14142B',
                  }}
                >
                  Order Ad Hoc Screening Reports
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="left" mt={2}>
                <FormControl sx={{ m: 1 }} component="fieldset" variant="standard">
                  <FormLabel component="legend">Select Applicants</FormLabel>
                  <FormGroup>
                    {availableScreenees?.map((sr) => {
                      return (
                        <FormControlLabel
                          value={sr.Uuid}
                          control={<Checkbox checked={sr.IsSelected} onChange={handleScreeneeSelectionCheckboxChange} name={sr.Uuid} />}
                          key={sr.Uuid}
                          style={{ paddingBottom: 0, paddingLeft: 10 }}
                          labelPlacement="end"
                          label={<Typography>{sr.DisplayName}</Typography>}
                        />
                      );
                    })}
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="left" mt={2}>
                <FormControl sx={{ m: 1 }} component="fieldset" variant="standard">
                  <FormLabel component="legend">Select Reports</FormLabel>
                  <FormGroup>
                    {availableScreeningProducts?.map((fp) => {
                      return (
                        <FormControlLabel
                          value={fp.Id}
                          control={<Checkbox disabled={!!fp.PurchasedDate} checked={fp.IsSelected} onChange={handleProductSelectionCheckboxChange} name={fp.Id} />}
                          key={fp.Id}
                          style={{ paddingBottom: 20, paddingLeft: 10 }}
                          labelPlacement="end"
                          label={
                            <Typography>
                              {fp.Label}
                              <br />
                              <NumberFormat
                                thousandsGroupStyle="thousand"
                                value={convertCentsToDollars(fp.Cost)}
                                prefix="$"
                                decimalSeparator="."
                                displayType="text"
                                type="text"
                                thousandSeparator={true}
                                allowNegative={true}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                            </Typography>
                          }
                        />
                      );
                    })}
                  </FormGroup>
                </FormControl>
              </Grid>
              {/* Save button */}
              {!isApplicationCompleted && <Typography className={classes.globalText}>Screening orders cannot be processed on incomplete applications</Typography>}
              <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <LoadingButton
                    variant="contained"
                    disabled={!orderButtonEnabled || !isApplicationCompleted}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 400,
                      fontFamily: 'Work Sans',
                      fontSize: '16px',
                      borderRadius: '24px',
                      backgroundColor: '#39A2DB',
                    }}
                    onClick={() => {
                      moveToConfirmationStep();
                    }}
                  >
                    Order
                  </LoadingButton>
                </Box>
              </Box>
              {/* Close Icon */}
              <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
                <img src={CancelIcon} alt="close" style={{ cursor: 'pointer' }} onClick={props.onClose} />
              </Grid>
            </Grid>
          )}
          {orderStep === 2 && propGroupConfigs.ScreeningEnabled && (
            <Grid container width="100%" display="flex" justifyContent="center">
              <Grid item xs={12} display="flex" justifyContent="center">
                <Typography
                  sx={{
                    fontFamily: 'Work Sans !important',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    fontSize: '18px !important',
                    lineHeight: '21px !important',
                    color: '#14142B',
                  }}
                >
                  Confirm Purchase
                </Typography>
              </Grid>
              {globalError && (
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Typography className={classes.globalText}>{globalError}</Typography>
                  {/* <Typography >{globalError}</Typography> */}
                </Grid>
              )}
              <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                <img src={DollarSign} alt="Confirm" style={{ width: '84px' }} />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                <Typography>
                  Please confirm you want to proceed with an order of &nbsp;
                  <NumberFormat
                    style={{ fontWeight: 'bolder' }}
                    thousandsGroupStyle="thousand"
                    value={convertCentsToDollars(availableScreenees.filter((sc) => !!sc.IsSelected).length * newItemsToPurchase.map((np) => np.Cost).reduce((prev, next) => prev + next, 0))}
                    prefix="$"
                    decimalSeparator="."
                    displayType="text"
                    type="text"
                    thousandSeparator={true}
                    allowNegative={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                  />
                  . This purchase will be added to your monthly invoice.
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="left" mt={2}>
                <Typography>Applicants:</Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="left">
                <ul style={{ marginTop: 2 }}>
                  {availableScreenees
                    .filter((sc) => !!sc.IsSelected)
                    .map((ss) => {
                      return (
                        <li key={ss.Uuid}>
                          <Typography>{ss.DisplayName}</Typography>
                        </li>
                      );
                    })}
                </ul>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="left">
                <Typography>Reports (each):</Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="left" pb={2}>
                <ul style={{ marginTop: 2 }}>
                  {newItemsToPurchase.map((np) => {
                    return (
                      <li key={np.Id}>
                        <Typography>
                          {np.Label} &nbsp;&nbsp;
                          <NumberFormat
                            thousandsGroupStyle="thousand"
                            value={convertCentsToDollars(np.Cost)}
                            prefix="$"
                            decimalSeparator="."
                            displayType="text"
                            type="text"
                            thousandSeparator={true}
                            allowNegative={true}
                            decimalScale={2}
                            fixedDecimalScale={true}
                          />
                        </Typography>
                      </li>
                    );
                  })}
                </ul>
              </Grid>
              {/* Save button */}
              <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <LoadingButton
                    variant="contained"
                    style={{
                      textTransform: 'none',
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      backgroundColor: '#39A2DB',
                      borderRadius: '24px',
                      width: '150px',
                    }}
                    onClick={() => {
                      submitOrder();
                    }}
                  >
                    Confirm
                  </LoadingButton>
                </Box>
              </Box>
              <Grid item sx={{ position: 'absolute', top: 18, left: 20 }}>
                <img src={back2} alt="close" style={{ cursor: 'pointer' }} onClick={() => setOrderStep(1)} />
              </Grid>
              {/* Close Icon */}
              <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
                <img src={CancelIcon} alt="close" style={{ cursor: 'pointer' }} onClick={props.onClose} />
              </Grid>
            </Grid>
          )}
          {orderStep === 3 && propGroupConfigs.ScreeningEnabled && (
            <Grid container width="100%" display="flex" justifyContent="center">
              <Grid item xs={12} display="flex" justifyContent="center">
                <Typography
                  sx={{
                    fontFamily: 'Work Sans !important',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    fontSize: '18px !important',
                    lineHeight: '21px !important',
                    color: '#14142B',
                  }}
                >
                  Order Submitted!
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                <img src={ConfirmIcon} alt="Confirmation" style={{ width: '84px' }} />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                <Typography>You will get notified when the order is completed. You can access the report on the applications dashboard.</Typography>
              </Grid>
              {/* Save button */}
              <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <LoadingButton
                    variant="contained"
                    style={{
                      textTransform: 'none',
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      backgroundColor: '#39A2DB',
                      borderRadius: '24px',
                      width: '150px',
                    }}
                    onClick={() => {
                      dispatch({ type: resetScreeningOrderState.type });
                      props.onClose();
                    }}
                  >
                    Close
                  </LoadingButton>
                </Box>
              </Box>
              {/* Close Icon */}
              <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
                <img src={CancelIcon} alt="close" style={{ cursor: 'pointer' }} onClick={props.onClose} />
              </Grid>
            </Grid>
          )}
          {!propGroupConfigs.ScreeningEnabled && (
            <Grid container width="100%" display="flex" justifyContent="center">
              <Grid item xs={12} display="flex" justifyContent="center">
                <Typography
                  sx={{
                    fontFamily: 'Work Sans !important',
                    fontStyle: 'normal',
                    fontWeight: '600',
                    fontSize: '18px !important',
                    lineHeight: '21px !important',
                    color: '#14142B',
                  }}
                >
                  Screening is not enabled
                </Typography>
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                <img src={ComingSoon} alt="Confirmation" style={{ width: '184px' }} />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="center" mt={2}>
                <Typography>
                  Looks like your company is not yet set up for Screening.
                  <br />
                  <br />
                  Do not fret. Please contact our support team and they will get you all squared away: <a href="mailto:pm-support@inosio.com">pm-support@inosio.com</a>{' '}
                </Typography>
              </Grid>
              {/* Save button */}
              <Box sx={{ position: 'absolute', bottom: 15, width: '94%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <LoadingButton
                    variant="contained"
                    style={{
                      textTransform: 'none',
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      backgroundColor: '#39A2DB',
                      borderRadius: '24px',
                      width: '150px',
                    }}
                    onClick={() => {
                      dispatch({ type: resetScreeningOrderState.type });
                      props.onClose();
                    }}
                  >
                    Close
                  </LoadingButton>
                </Box>
              </Box>
              {/* Close Icon */}
              <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
                <img src={CancelIcon} alt="close" style={{ cursor: 'pointer' }} onClick={props.onClose} />
              </Grid>
            </Grid>
          )}
        </Card>
      </Modal>
      {isSpinnerModalOpen ? (
        <SpinnerModal
          width="472px"
          height="523px"
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => setIsSpinnerModalOpen(false)}
        />
      ) : (
        <></>
      )}{' '}
    </div>
  );
}
